import { cx } from '@emotion/css';
import { useDebounceEffect, useThrottle } from 'ahooks';
import {
  useConversationInputDraft,
  useStateSafeConversationQuotedMsgDraft,
} from 'hooks/business/conversationDraft';
import { useMemo } from 'react';

const DEFAULT_INPUT_MAX = 600;
const INPUT_MAX_CHANNEL: Record<Conversation.Conversation['channel'], number> = {
  SHOPEE: 2000,
  LAZADA: 2000,
  TOKOPEDIA: 2000,
  WA_BIZ: 4000,
  TIKTOK: 2000,
  TIKTOK_US: 2000,
  ALIEXPRESS: 2000,
  MERCADO: 350,
  KWAI: 2000,
  FACEBOOK: 2000,
  JOLECT: 2000,
};

export interface InputCountProps {
  className?: string;
  conversation: Conversation.Conversation;
  onInputOverflowChange: (isInputOverflow: boolean) => void;
}

export const InputCount = (props: InputCountProps) => {
  const {
    className,
    conversation: { conversationId, channel },
    onInputOverflowChange,
  } = props;
  const quotedMsg = useStateSafeConversationQuotedMsgDraft(conversationId);
  const input = useConversationInputDraft(conversationId);
  const throttledInput = useThrottle(input, { wait: 100 });

  const inputLength = useMemo(() => {
    let count = 0;
    if (channel === 'SHOPEE') {
      count = quotedMsg?.textContent?.text ? quotedMsg.textContent.text.length + 35 : 0;
      try {
        // 解决shopee对字符长度处理不一的问题：https://www.teambition.com/task/6388728d0de329003ff4b90b
        // throttledInput 减少执行，优化性能
        // 当前版本的ts(4.3.4)对Intl.Segmenter定义不完整，compiler会报错
        // @ts-ignore
        const seg = new Intl.Segmenter().segment(throttledInput);
        count += throttledInput ? Array.from(seg).length : 0;
      } catch {
        count += throttledInput.length;
      }
    } else {
      count = throttledInput?.length;
    }

    return count || 0;
  }, [channel, throttledInput, quotedMsg]);

  const inputMaxLength = useMemo(() => {
    return INPUT_MAX_CHANNEL[channel] || DEFAULT_INPUT_MAX;
  }, [channel]);

  // 消息长度溢出判断
  const isInputOverflow = useMemo(() => {
    return inputLength > inputMaxLength;
  }, [inputLength, inputMaxLength]);

  useDebounceEffect(
    () => {
      onInputOverflowChange(isInputOverflow);
    },
    [isInputOverflow],
    { wait: 200 },
  );

  return (
    <div className={cx('text-[var(--fett-text-quaternary)]', className)}>
      <span className={isInputOverflow ? 'text-error' : ''}>{inputLength || 0}</span>/
      {inputMaxLength}
    </div>
  );
};
